import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Spinner
} from 'reactstrap';
import { Plus, Edit, Trash2, Server, Globe, Shield, Clock, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';

const EppCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [alert, setAlert] = useState(null);

  // EPP Settings Modal
  const [eppSettingsModal, setEppSettingsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [eppSettings, setEppSettings] = useState({
    // EPP Check Command
    check_command_type: 'rfc', // 'rfc' or 'custom'
    check_command_custom: '',

    // EPP Batch Check Command
    check_batch_command_type: 'rfc', // 'rfc' or 'custom'
    check_batch_command_custom: '',

    // EPP Create Command
    create_command_type: 'rfc', // 'rfc' or 'custom'
    create_command_custom: '',

    // Keep Alive Settings
    keepalive_enabled: false,
    keepalive_interval: 300, // seconds
    keepalive_type: 'hello', // 'hello', 'domain_check', 'custom'
    keepalive_domain: 'test.com', // for domain_check type
    keepalive_command_custom: '', // for custom type

    // Scheduling Settings
    connection_schedule_enabled: false,
    connection_schedule_time: '',
    connection_schedule_timezone: 'UTC',
    order_processing_schedule_enabled: false,
    order_processing_schedule_time: '',
    order_processing_schedule_timezone: 'UTC',

    // Workflow Settings
    capture_strategy: 'check_then_create', // 'check_then_create' or 'direct_create'
    command_delay_ms: 0, // milliseconds
    create_mode: 'wait_response', // 'wait_response' or 'fire_and_forget'

    // Rate limiting
    max_commands_per_second: 10,
    max_concurrent_commands: 5,
    // Domain check batch settings
    domain_check_batch_size: 1,
    domain_check_multicheck_enabled: false
  });
  const [formData, setFormData] = useState({
    name: '',
    epp_server_url: '',
    epp_port: 700,
    description: '',
    supported_tlds: '',
    default_timeout: 30,
    requires_ssl_cert: false,
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await API.get('/epp/categories');
      setCategories(response.data.categories);
    } catch (error) {
      showAlert('danger', 'Failed to load EPP categories: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (variant, message) => {
    setAlert({ variant, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      epp_server_url: '',
      epp_port: 700,
      description: '',
      supported_tlds: '',
      default_timeout: 30,
      requires_ssl_cert: false,
      is_active: true
    });
    setEditingCategory(null);
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        epp_server_url: category.epp_server_url || '',
        epp_port: category.epp_port || 700,
        description: category.description || '',
        supported_tlds: Array.isArray(category.supported_tlds)
          ? category.supported_tlds.join(', ')
          : category.supported_tlds || '',
        default_timeout: category.default_timeout || 30,
        requires_ssl_cert: category.requires_ssl_cert || false,
        is_active: category.is_active !== false
      });
    } else {
      resetForm();
    }
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    resetForm();
  };

  const openEppSettings = async (category) => {
    setSelectedCategory(category);

    try {
      // Load existing settings
      const response = await API.get(`/epp/settings/${category.id}`);
      setEppSettings(response.data.settings);
    } catch (error) {
      // If no settings exist, use defaults
      console.log('Using default settings for new category');
    }

    setEppSettingsModal(true);
  };

  const closeEppSettings = () => {
    setEppSettingsModal(false);
    setSelectedCategory(null);
    // Reset to default settings
    setEppSettings({
      check_command_type: 'rfc',
      check_command_custom: '',
      create_command_type: 'rfc',
      create_command_custom: '',
      keepalive_enabled: false,
      keepalive_interval: 300,
      keepalive_type: 'hello',
      keepalive_domain: 'test.com',
      keepalive_command_custom: ''
    });
  };

  const handleEppSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEppSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEppSettingsSave = async () => {
    try {
      await API.put(`/epp/settings/${selectedCategory.id}`, eppSettings);
      showAlert('success', 'EPP settings saved successfully');
      closeEppSettings();
    } catch (error) {
      showAlert('danger', 'Failed to save EPP settings: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = {
        ...formData,
        epp_port: parseInt(formData.epp_port),
        default_timeout: parseInt(formData.default_timeout),
        supported_tlds: formData.supported_tlds
          ? formData.supported_tlds.split(',').map(tld => tld.trim()).filter(tld => tld)
          : []
      };

      if (editingCategory) {
        await API.put(`/epp/categories/${editingCategory.id}`, submitData);
        showAlert('success', 'EPP category updated successfully');
      } else {
        await API.post('/epp/categories', submitData);
        showAlert('success', 'EPP category created successfully');
      }

      closeModal();
      loadCategories();
    } catch (error) {
      showAlert('danger', 'Failed to save category: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this EPP category? This action cannot be undone.')) {
      return;
    }

    try {
      await API.delete(`/epp/categories/${categoryId}`);
      showAlert('success', 'EPP category deleted successfully');
      loadCategories();
    } catch (error) {
      showAlert('danger', 'Failed to delete category: ' + (error.response?.data?.error || error.message));
    }
  };

  // Only admins can access this page
  if (user?.role !== 'admin') {
    return (
      <Container className="mt-4">
        <Alert variant="warning" title="Access Denied">
          Only administrators can manage EPP registrar categories.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <i className="fas fa-sitemap page-heading-icon"></i>
            EPP Registrar Categories
          </h2>
          <small className="text-muted">Configure EPP registrar categories and server settings</small>
        </Col>
        <Col xs="auto">
          <Button color="primary" size="sm" onClick={() => openModal()}>
            <Plus size={16} className="me-1" /> Add Category
          </Button>
        </Col>
      </Row>

      <Card className="bg-dark border-secondary">
        <div className="card-body">
              {alert && (
                <div className={`alert alert-${alert.variant} alert-dismissible fade show`} role="alert">
                  {alert.message}
                  <button type="button" className="btn-close-enhanced" onClick={() => setAlert(null)}>×</button>
                </div>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">No EPP Categories</h5>
                  <p className="text-muted">Create your first EPP registrar category to get started.</p>
                </div>
              ) : (
                <Table dark hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>EPP Server</th>
                      <th>Port</th>
                      <th>TLDs</th>
                      <th>SSL Required</th>
                      <th>Timeout</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map(category => (
                      <tr key={category.id}>
                        <td>
                          <strong>{category.name}</strong>
                          {category.description && (
                            <small className="d-block text-muted">{category.description}</small>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Globe size={14} className="text-muted me-2" />
                            <code className="small">{category.epp_server_url}</code>
                          </div>
                        </td>
                        <td>{category.epp_port}</td>
                        <td>
                          {Array.isArray(category.supported_tlds) && category.supported_tlds.length > 0 ? (
                            <div>
                              {category.supported_tlds.slice(0, 3).map(tld => (
                                <Badge key={tld} className="bg-dark text-info border border-info me-1 mb-1">
                                  {tld}
                                </Badge>
                              ))}
                              {category.supported_tlds.length > 3 && (
                                <Badge className="bg-dark text-light border border-secondary">
                                  +{category.supported_tlds.length - 3}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted">Any</span>
                          )}
                        </td>
                        <td>
                          {category.requires_ssl_cert ? (
                            <Badge className="bg-dark text-warning border border-warning">
                              <Shield size={12} className="me-1" />
                              Required
                            </Badge>
                          ) : (
                            <Badge className="bg-dark text-light border border-secondary">Optional</Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Clock size={12} className="text-muted me-2" />
                            <span className="small">{category.default_timeout}s</span>
                          </div>
                        </td>
                        <td>
                          {category.is_active ? (
                            <Badge className="bg-dark text-success border border-success">Active</Badge>
                          ) : (
                            <Badge className="bg-dark text-danger border border-danger">Inactive</Badge>
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="outline-info"
                              size="sm"
                              onClick={() => openEppSettings(category)}
                              title="EPP Settings"
                              className="me-1"
                            >
                              <Settings size={14} />
                            </Button>
                            <Button
                              color="outline-primary"
                              size="sm"
                              onClick={() => openModal(category)}
                              title="Edit"
                              className="me-1"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(category.id)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} toggle={closeModal} size="lg" className="modal-dark">
        <ModalHeader toggle={closeModal} className="bg-dark text-white border-secondary">
          <i className="fas fa-sitemap me-2"></i>
          {editingCategory ? 'Edit EPP Category' : 'Add EPP Category'}
        </ModalHeader>
        <ModalBody className="bg-dark text-white">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="name">
                    Category Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., GoDaddy Registry"
                    className="bg-secondary border-secondary text-white"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="epp_server_url">
                    EPP Server Domain <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="epp_server_url"
                    name="epp_server_url"
                    value={formData.epp_server_url}
                    onChange={handleInputChange}
                    placeholder="epp.example.com"
                    pattern="^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$"
                    title="Enter a valid domain name (e.g., epp.example.com)"
                    className="bg-secondary border-secondary text-white"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="epp_port">EPP Port</Label>
                  <Input
                    type="number"
                    id="epp_port"
                    name="epp_port"
                    value={formData.epp_port}
                    onChange={handleInputChange}
                    min="1"
                    max="65535"
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="default_timeout">Default Timeout (seconds)</Label>
                  <Input
                    type="number"
                    id="default_timeout"
                    name="default_timeout"
                    value={formData.default_timeout}
                    onChange={handleInputChange}
                    min="5"
                    max="300"
                    className="bg-secondary border-secondary text-white"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup check className="mt-4">
                  <Input
                    type="checkbox"
                    id="requires_ssl_cert"
                    name="requires_ssl_cert"
                    checked={formData.requires_ssl_cert}
                    onChange={handleInputChange}
                  />
                  <Label check for="requires_ssl_cert">
                    Requires SSL Certificate
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Optional description of this registrar category..."
                rows={2}
                className="bg-secondary border-secondary text-white"
              />
            </FormGroup>

            <FormGroup>
              <Label for="supported_tlds">
                Supported TLDs
                <small className="text-muted d-block">(comma-separated, leave empty for all TLDs)</small>
              </Label>
              <Input
                type="text"
                id="supported_tlds"
                name="supported_tlds"
                value={formData.supported_tlds}
                onChange={handleInputChange}
                placeholder="com, net, org, info, biz"
                className="bg-secondary border-secondary text-white"
              />
            </FormGroup>

            <FormGroup check>
              <Input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              <Label check for="is_active">
                Active (can be used for new accounts)
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="bg-dark border-secondary">
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            {editingCategory ? 'Update Category' : 'Create Category'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* EPP Settings Modal */}
      <Modal isOpen={eppSettingsModal} toggle={closeEppSettings} size="xl" className="modal-dark">
        <ModalHeader toggle={closeEppSettings} className="bg-dark text-white border-secondary">
          <Settings className="me-2" size={20} />
          EPP Settings - {selectedCategory?.name}
        </ModalHeader>
        <ModalBody className="bg-dark text-white">
          <Row>
            {/* EPP Check Command */}
            <Col md={4} className="mb-4">
              <Card className="bg-secondary border-secondary">
                <div className="card-body">
                  <h6 className="text-light mb-3">
                    <Server size={16} className="me-2" />
                    EPP Check Command
                  </h6>

                  <FormGroup>
                    <Label>Command Type</Label>
                    <Input
                      type="select"
                      name="check_command_type"
                      value={eppSettings.check_command_type}
                      onChange={handleEppSettingsChange}
                      className="bg-dark border-dark text-white"
                    >
                      <option value="rfc">RFC Standard</option>
                      <option value="custom">Custom Template</option>
                    </Input>
                  </FormGroup>

                  {eppSettings.check_command_type === 'custom' && (
                    <FormGroup>
                      <Label>
                        Custom Command Template
                        <small className="text-muted d-block">
                          Available parameters: {'{XMLNS_DOMAIN}'}, {'{DOMAIN}'}, {'{CLTRID}'}
                        </small>
                      </Label>
                      <Input
                        type="textarea"
                        name="check_command_custom"
                        value={eppSettings.check_command_custom}
                        onChange={handleEppSettingsChange}
                        placeholder={`<?xml version="1.0" encoding="UTF-8"?>
<epp xmlns="urn:ietf:params:xml:ns:epp-1.0">
  <command>
    <check>
      <domain:check xmlns:domain="{XMLNS_DOMAIN}">
        <domain:name>{DOMAIN}</domain:name>
      </domain:check>
    </check>
    <clTRID>{CLTRID}</clTRID>
  </command>
</epp>`}
                        rows={8}
                        className="bg-dark border-dark text-white font-monospace"
                        style={{ fontSize: '12px' }}
                      />
                    </FormGroup>
                  )}
                </div>
              </Card>
            </Col>

            {/* EPP Batch Check Command */}
            <Col md={4} className="mb-4">
              <Card className="bg-secondary border-secondary">
                <div className="card-body">
                  <h6 className="text-light mb-3">
                    <Server size={16} className="me-2" />
                    EPP Batch Check Command
                  </h6>

                  <FormGroup>
                    <Label>Command Type</Label>
                    <Input
                      type="select"
                      name="check_batch_command_type"
                      value={eppSettings.check_batch_command_type}
                      onChange={handleEppSettingsChange}
                      className="bg-dark border-dark text-white"
                    >
                      <option value="rfc">RFC Standard</option>
                      <option value="custom">Custom Template</option>
                    </Input>
                  </FormGroup>

                  {eppSettings.check_batch_command_type === 'custom' && (
                    <FormGroup>
                      <Label>
                        Custom Batch Command Template
                        <small className="text-muted d-block">
                          Available parameters: {'{XMLNS_DOMAIN}'}, {'{DOMAINS_XML}'}, {'{DOMAINS_LIST}'}, {'{CLTRID}'}
                        </small>
                      </Label>
                      <Input
                        type="textarea"
                        name="check_batch_command_custom"
                        value={eppSettings.check_batch_command_custom}
                        onChange={handleEppSettingsChange}
                        placeholder={`<?xml version="1.0" encoding="UTF-8"?>
<epp xmlns="urn:ietf:params:xml:ns:epp-1.0"
     xmlns:domain="{XMLNS_DOMAIN}">
  <command>
    <check>
      <domain:check>
{DOMAINS_XML}
      </domain:check>
    </check>
    <clTRID>{CLTRID}</clTRID>
  </command>
</epp>

Note: {DOMAINS_XML} already contains individual <domain:name> elements, don't wrap them again!`}
                        rows={8}
                        className="bg-dark border-dark text-white font-monospace"
                        style={{ fontSize: '12px' }}
                      />
                    </FormGroup>
                  )}
                </div>
              </Card>
            </Col>

            {/* EPP Create Command */}
            <Col md={4} className="mb-4">
              <Card className="bg-secondary border-secondary">
                <div className="card-body">
                  <h6 className="text-light mb-3">
                    <Plus size={16} className="me-2" />
                    EPP Create Command
                  </h6>

                  <FormGroup>
                    <Label>Command Type</Label>
                    <Input
                      type="select"
                      name="create_command_type"
                      value={eppSettings.create_command_type}
                      onChange={handleEppSettingsChange}
                      className="bg-dark border-dark text-white"
                    >
                      <option value="rfc">RFC Standard</option>
                      <option value="custom">Custom Template</option>
                    </Input>
                  </FormGroup>

                  {eppSettings.create_command_type === 'custom' && (
                    <FormGroup>
                      <Label>
                        Custom Command Template
                        <small className="text-muted d-block">
                          Available parameters: {'{XMLNS_DOMAIN}'}, {'{DOMAIN}'}, {'{CLTRID}'}
                        </small>
                      </Label>
                      <Input
                        type="textarea"
                        name="create_command_custom"
                        value={eppSettings.create_command_custom}
                        onChange={handleEppSettingsChange}
                        placeholder={`<?xml version="1.0" encoding="UTF-8"?>
<epp xmlns="urn:ietf:params:xml:ns:epp-1.0">
  <command>
    <create>
      <domain:create xmlns:domain="{XMLNS_DOMAIN}">
        <domain:name>{DOMAIN}</domain:name>
        <domain:period unit="y">1</domain:period>
      </domain:create>
    </create>
    <clTRID>{CLTRID}</clTRID>
  </command>
</epp>`}
                        rows={8}
                        className="bg-dark border-dark text-white font-monospace"
                        style={{ fontSize: '12px' }}
                      />
                    </FormGroup>
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col md={12} className="mb-4">
              {/* Keep Alive Settings */}
              <Card className="bg-secondary border-secondary">
            <div className="card-body">
              <h6 className="text-light mb-3">
                <Clock size={16} className="me-2" />
                Keep Alive Settings
              </h6>

              <Row>
                <Col md={4}>
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      id="keepalive_enabled"
                      name="keepalive_enabled"
                      checked={eppSettings.keepalive_enabled}
                      onChange={handleEppSettingsChange}
                    />
                    <Label check for="keepalive_enabled">
                      Enable Keep Alive
                    </Label>
                  </FormGroup>
                </Col>

                {eppSettings.keepalive_enabled && (
                  <>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Interval (seconds)</Label>
                        <Input
                          type="number"
                          name="keepalive_interval"
                          value={eppSettings.keepalive_interval}
                          onChange={handleEppSettingsChange}
                          min="60"
                          max="3600"
                          className="bg-dark border-dark text-white"
                        />
                      </FormGroup>
                    </Col>

                    <Col md={4}>
                      <FormGroup>
                        <Label>Keep Alive Type</Label>
                        <Input
                          type="select"
                          name="keepalive_type"
                          value={eppSettings.keepalive_type}
                          onChange={handleEppSettingsChange}
                          className="bg-dark border-dark text-white"
                        >
                          <option value="hello">Hello Command</option>
                          <option value="domain_check">Domain Check</option>
                          <option value="custom">Custom Command</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </>
                )}
              </Row>

              {eppSettings.keepalive_enabled && eppSettings.keepalive_type === 'domain_check' && (
                <FormGroup>
                  <Label>Test Domain for Keep Alive</Label>
                  <Input
                    type="text"
                    name="keepalive_domain"
                    value={eppSettings.keepalive_domain}
                    onChange={handleEppSettingsChange}
                    placeholder="test.com"
                    className="bg-dark border-dark text-white"
                  />
                </FormGroup>
              )}

              {eppSettings.keepalive_enabled && eppSettings.keepalive_type === 'custom' && (
                <FormGroup>
                  <Label>
                    Custom Keep Alive Command
                    <small className="text-muted d-block">
                      Available parameters: {'{XMLNS_DOMAIN}'}, {'{DOMAIN}'}, {'{CLTRID}'}
                    </small>
                  </Label>
                  <Input
                    type="textarea"
                    name="keepalive_command_custom"
                    value={eppSettings.keepalive_command_custom}
                    onChange={handleEppSettingsChange}
                    placeholder={`<?xml version="1.0" encoding="UTF-8"?>
<epp xmlns="urn:ietf:params:xml:ns:epp-1.0">
  <hello/>
</epp>`}
                    rows={6}
                    className="bg-dark border-dark text-white font-monospace"
                    style={{ fontSize: '12px' }}
                  />
                </FormGroup>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* Scheduling Settings */}
        <Col md={6} className="mb-4">
          <Card className="bg-secondary border-secondary">
            <div className="card-body">
              <h6 className="text-light mb-3">
                <Clock size={16} className="me-2" />
                Scheduling Settings
              </h6>

              {/* Connection Schedule */}
              <FormGroup>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="connection_schedule_enabled"
                    checked={eppSettings.connection_schedule_enabled}
                    onChange={handleEppSettingsChange}
                  />
                  <label className="form-check-label text-light">
                    Enable Connection Schedule
                  </label>
                </div>
              </FormGroup>

              {eppSettings.connection_schedule_enabled && (
                <Row>
                  <Col md={8}>
                    <FormGroup>
                      <Label>Connection Time (HH:MM:SS)</Label>
                      <Input
                        type="text"
                        name="connection_schedule_time"
                        value={eppSettings.connection_schedule_time}
                        onChange={handleEppSettingsChange}
                        placeholder="09:00:00"
                        className="bg-dark border-dark text-white"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Timezone</Label>
                      <Input
                        type="select"
                        name="connection_schedule_timezone"
                        value={eppSettings.connection_schedule_timezone}
                        onChange={handleEppSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="UTC">UTC</option>
                        <option value="Europe/Prague">Europe/Prague</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              )}

              {/* Order Processing Schedule */}
              <FormGroup className="mt-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="order_processing_schedule_enabled"
                    checked={eppSettings.order_processing_schedule_enabled}
                    onChange={handleEppSettingsChange}
                  />
                  <label className="form-check-label text-light">
                    Enable Order Processing Schedule
                  </label>
                </div>
              </FormGroup>

              {eppSettings.order_processing_schedule_enabled && (
                <Row>
                  <Col md={8}>
                    <FormGroup>
                      <Label>Processing Time (HH:MM:SS)</Label>
                      <Input
                        type="text"
                        name="order_processing_schedule_time"
                        value={eppSettings.order_processing_schedule_time}
                        onChange={handleEppSettingsChange}
                        placeholder="10:00:00"
                        className="bg-dark border-dark text-white"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Timezone</Label>
                      <Input
                        type="select"
                        name="order_processing_schedule_timezone"
                        value={eppSettings.order_processing_schedule_timezone}
                        onChange={handleEppSettingsChange}
                        className="bg-dark border-dark text-white"
                      >
                        <option value="UTC">UTC</option>
                        <option value="Europe/Prague">Europe/Prague</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </div>
          </Card>
        </Col>

        {/* Workflow Settings */}
        <Col md={6} className="mb-4">
          <Card className="bg-secondary border-secondary">
            <div className="card-body">
              <h6 className="text-light mb-3">
                <Globe size={16} className="me-2" />
                Workflow Settings
              </h6>

              <FormGroup>
                <Label>Capture Strategy</Label>
                <Input
                  type="select"
                  name="capture_strategy"
                  value={eppSettings.capture_strategy}
                  onChange={handleEppSettingsChange}
                  className="bg-dark border-dark text-white"
                >
                  <option value="check_then_create">Check then Create</option>
                  <option value="direct_create">Direct Create</option>
                </Input>
                <small className="text-muted">
                  Check then Create: First check domain availability, then create if available<br/>
                  Direct Create: Directly attempt to create domain
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Command Delay (milliseconds)</Label>
                <Input
                  type="number"
                  name="command_delay_ms"
                  value={eppSettings.command_delay_ms}
                  onChange={handleEppSettingsChange}
                  min="0"
                  max="300000"
                  className="bg-dark border-dark text-white"
                />
                <small className="text-muted">Delay between EPP commands (0-300000 ms, max 5 minutes)</small>
              </FormGroup>

              <FormGroup>
                <Label>Create Mode</Label>
                <Input
                  type="select"
                  name="create_mode"
                  value={eppSettings.create_mode}
                  onChange={handleEppSettingsChange}
                  className="bg-dark border-dark text-white"
                >
                  <option value="wait_response">Wait for Response</option>
                  <option value="fire_and_forget">Fire and Forget</option>
                </Input>
                <small className="text-muted">
                  Wait for Response: Wait for EPP response before continuing<br/>
                  Fire and Forget: Send command without waiting for response
                </small>
              </FormGroup>
            </div>
          </Card>
        </Col>

        {/* Rate Limiting Settings */}
        <Col md={6} className="mb-4">
          <Card className="bg-secondary border-secondary">
            <div className="card-body">
              <h6 className="text-light mb-3">
                <Shield size={16} className="me-2" />
                Rate Limiting
              </h6>

              <FormGroup>
                <Label>Max Commands per Second</Label>
                <Input
                  type="number"
                  name="max_commands_per_second"
                  value={eppSettings.max_commands_per_second}
                  onChange={handleEppSettingsChange}
                  min="1"
                  max="100"
                  className="bg-dark border-dark text-white"
                />
                <small className="text-muted">Maximum EPP commands per second (1-100)</small>
              </FormGroup>

              <FormGroup>
                <Label>Max Concurrent Commands</Label>
                <Input
                  type="number"
                  name="max_concurrent_commands"
                  value={eppSettings.max_concurrent_commands}
                  onChange={handleEppSettingsChange}
                  min="1"
                  max="50"
                  className="bg-dark border-dark text-white"
                />
                <small className="text-muted">Maximum concurrent EPP commands (1-50)</small>
              </FormGroup>
            </div>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card className="bg-secondary border-secondary">
            <div className="card-body">
              <h6 className="text-light mb-3">
                <Globe size={16} className="me-2" />
                Domain Check Settings
              </h6>

              <FormGroup check>
                <Input
                  type="checkbox"
                  name="domain_check_multicheck_enabled"
                  checked={eppSettings.domain_check_multicheck_enabled}
                  onChange={handleEppSettingsChange}
                  className="me-2"
                />
                <Label check>Enable Batch Domain Checking</Label>
                <small className="text-muted d-block">Allow multiple domains per EPP check command</small>
              </FormGroup>

              {eppSettings.domain_check_multicheck_enabled && (
                <FormGroup className="mt-3">
                  <Label>Batch Size</Label>
                  <Input
                    type="number"
                    name="domain_check_batch_size"
                    value={eppSettings.domain_check_batch_size}
                    onChange={handleEppSettingsChange}
                    min="1"
                    max="100"
                    className="bg-dark border-dark text-white"
                  />
                  <small className="text-muted">Number of domains per check command (1-100)</small>
                </FormGroup>
              )}
            </div>
          </Card>
        </Col>
      </Row>
      </ModalBody>
        <ModalFooter className="bg-dark border-secondary">
          <Button color="secondary" onClick={closeEppSettings}>
            Cancel
          </Button>
          <Button color="success" onClick={handleEppSettingsSave}>
            Save EPP Settings
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default EppCategories;